/**
 * Source-document library for the Brand Bible file intake (BB-FILE-DROP-01,
 * ADDENDUM 1 / slice B-FE-2). The persistent "where the dropped files live"
 * surface: every uploaded doc with a preview, extraction status, and a jump to
 * the drafts it produced.
 *
 * Presentational only — it owns no transport. The caller (B-FE-2 wiring, once
 * the B-BE list/fetch endpoints are pinned in the contract addendum) fetches
 * the manifest, maps it to SourceDoc rows, and supplies onOpen/onJumpToDrafts/
 * onDelete. Keeping it transport-free means the ingest endpoint shapes cannot
 * leak into presentation, same discipline as FileDropZone (f4dcbc560). Not
 * mounted anywhere yet.
 */
import { FileText, Image as ImageIcon, Loader2, CheckCircle2, XCircle, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';

export type SourceDocStatus = 'stored' | 'queued' | 'reading' | 'done' | 'error';

export interface SourceDoc {
  id: string;
  name: string;
  mime: string;
  /** Bytes; rendered human-readable. */
  size: number;
  uploadedBy?: string;
  /** ISO timestamp. */
  uploadedAt: string;
  status: SourceDocStatus;
  /** Drafts this file produced (present once status === 'done'). */
  draftCount?: number;
  /** Short text snippet / caption for the preview; caller-provided. */
  previewText?: string;
  /** Thumbnail URL (image mimes / rendered PDF page); caller-provided. */
  thumbUrl?: string;
  /** Server error verbatim when status === 'error'. */
  error?: string;
}

const STATUS_LABEL: Record<SourceDocStatus, string> = {
  stored: 'Stored',
  queued: 'Queued',
  reading: 'Reading…',
  done: 'Extracted',
  error: 'Failed',
};

export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/** Date only, locale-stable enough for a manifest row (no time-of-day noise). */
export function formatUploadedAt(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

const isImage = (mime: string) => mime.startsWith('image/');

/** E7: parsed + 0 drafts is not Done / Extracted. */
function zeroYieldLabel(doc: SourceDoc): string {
  if (doc.status === 'done' && doc.draftCount === 0) return 'Could not use this';
  if (doc.status === 'done' && doc.draftCount != null) {
    return `${doc.draftCount} ${doc.draftCount === 1 ? 'draft' : 'drafts'}`;
  }
  return STATUS_LABEL[doc.status];
}

function StatusPill({ doc }: { doc: SourceDoc }) {
  const unused = doc.status === 'done' && doc.draftCount === 0;
  return (
    <span
      className={cn(
        'flex flex-none items-center gap-1 text-[11px] font-semibold',
        doc.status === 'done' && !unused && 'text-coral-dark',
        unused && 'text-muted-foreground',
        doc.status === 'error' && 'text-destructive',
        doc.status === 'reading' && 'text-teal-dark',
        (doc.status === 'queued' || unused) && 'text-muted-foreground',
      )}
    >
      {doc.status === 'reading' && <Loader2 className="size-3 animate-spin" />}
      {doc.status === 'done' && (doc.draftCount ?? 0) > 0 && <CheckCircle2 className="size-3" />}
      {doc.status === 'error' && <XCircle className="size-3" />}
      {zeroYieldLabel(doc)}
    </span>
  );
}

export function SourceDocLibrary({
  docs,
  onOpen,
  onJumpToDrafts,
  onDelete,
}: {
  docs: SourceDoc[];
  onOpen: (id: string) => void;
  /** Jump into the needs-attention queue filtered to this file's drafts. */
  onJumpToDrafts?: (id: string) => void;
  onDelete?: (id: string) => void;
}) {
  if (docs.length === 0) {
    return (
      <div
        data-testid="bb-sourcedocs-empty"
        className="rounded-lg border border-dashed border-border bg-warm-bg px-6 py-8 text-center text-[12.5px] text-muted-foreground"
      >
        No source documents yet. Upload a brand guide, service sheet, or past proposal above.
      </div>
    );
  }

  return (
    <div
      data-testid="bb-sourcedocs-grid"
      className="grid grid-cols-2 gap-3 lg:grid-cols-3"
    >
      {docs.map((doc) => (
        <div
          key={doc.id}
          className="flex flex-col overflow-hidden rounded-lg border border-border bg-card"
        >
          <button
            type="button"
            onClick={() => onOpen(doc.id)}
            aria-label={`Preview ${doc.name}`}
            className="flex h-28 items-center justify-center overflow-hidden border-b border-border bg-warm-bg"
          >
            {doc.thumbUrl ? (
              <img src={doc.thumbUrl} alt="" className="size-full object-cover" />
            ) : doc.previewText ? (
              <p className="line-clamp-4 px-3 py-2 text-left text-[11px] leading-snug text-muted-foreground">
                {doc.previewText}
              </p>
            ) : isImage(doc.mime) ? (
              <ImageIcon className="size-7 text-muted-foreground" />
            ) : (
              <FileText className="size-7 text-muted-foreground" />
            )}
          </button>

          <div className="flex flex-1 flex-col gap-1 p-2.5">
            <div className="flex items-start gap-1.5">
              <span className="min-w-0 flex-1 truncate text-[12.5px] font-medium" title={doc.name}>
                {doc.name}
              </span>
              {onDelete && (
                <button
                  type="button"
                  aria-label={`Remove ${doc.name}`}
                  onClick={() => onDelete(doc.id)}
                  className="flex-none text-muted-foreground hover:text-destructive"
                >
                  <Trash2 className="size-3.5" />
                </button>
              )}
            </div>

            <div className="flex items-center gap-1.5 text-[10.5px] text-muted-foreground">
              <span>{formatBytes(doc.size)}</span>
              <span aria-hidden>·</span>
              <span>{formatUploadedAt(doc.uploadedAt)}</span>
              {doc.uploadedBy && (
                <>
                  <span aria-hidden>·</span>
                  <span className="truncate">{doc.uploadedBy}</span>
                </>
              )}
            </div>

            {doc.status === 'error' && doc.error && (
              <p className="text-[10.5px] text-destructive" title={doc.error}>
                {doc.error}
              </p>
            )}

            <div className="mt-auto flex items-center justify-between gap-2 pt-1">
              <StatusPill doc={doc} />
              {doc.status === 'done' && (doc.draftCount ?? 0) > 0 && onJumpToDrafts && (
                <button
                  type="button"
                  onClick={() => onJumpToDrafts(doc.id)}
                  className="text-[11px] font-semibold text-coral hover:text-coral-dark"
                >
                  Review →
                </button>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
